import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ScanFace, Camera, Check, ArrowLeft, Loader2, Lock, ImagePlus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAN_IMG = "https://static.prod-images.emergentagent.com/jobs/25bdb5cb-a38e-4afc-ae48-06a4162d0c2e/images/3d734dad3d74353992573cf4ae65b9edf63fb0eb331a0e81ee254e6b66b3b92b.jpeg";
const WOMAN_IMG = "https://static.prod-images.emergentagent.com/jobs/25bdb5cb-a38e-4afc-ae48-06a4162d0c2e/images/64df4c648c7812305c69559b5c938c099f11c49d3c79dc25e2e3f96327b8f424.jpeg";

function compress(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const max = 1024;
        let { width, height } = img;
        if (width > height && width > max) { height = (height * max) / width; width = max; }
        else if (height > max) { width = (width * max) / height; height = max; }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.75));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const PhotoInput = ({ id, label, hint, value, onChange }) => {
  const ref = useRef();
  return (
    <div className="flex-1">
      <input ref={ref} data-testid={`input-${id}`} type="file" accept="image/*" capture="user" className="hidden"
        onChange={async (e) => { const f = e.target.files?.[0]; if (f) { try { onChange(await compress(f)); } catch { toast.error("Erro ao processar a imagem"); } } }} />
      <button data-testid={`btn-${id}`} onClick={() => ref.current?.click()}
        className={`w-full aspect-[3/4] rounded-2xl border-2 border-dashed flex flex-col items-center justify-center gap-3 overflow-hidden transition-colors ${value ? "border-[#C8A46A]" : "border-white/15 hover:border-[#C8A46A]"}`}>
        {value ? (
          <div className="relative w-full h-full">
            <img src={value} alt={label} className="w-full h-full object-cover" />
            <div className="absolute top-2 right-2 bg-[#C8A46A] text-[#0F0F10] rounded-full p-1.5"><Check className="w-4 h-4" /></div>
          </div>
        ) : (
          <>
            <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center"><ImagePlus className="w-6 h-6 text-[#C8A46A]" /></div>
            <div className="text-center px-4">
              <p className="font-medium text-[#F5F1E8]">{label}</p>
              <p className="text-xs text-[#F5F1E8]/50 mt-1">{hint}</p>
            </div>
          </>
        )}
      </button>
    </div>
  );
};

export default function Comecar() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [gender, setGender] = useState(null);
  const [front, setFront] = useState(null);
  const [profile, setProfile] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [price, setPrice] = useState(67);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/config`).then(({ data }) => data?.price_brl && setPrice(data.price_brl)).catch(() => {});
  }, []);

  const pickGender = (g) => { setGender(g); setStep(2); };

  const checkout = async () => {
    if (!front || !profile) { toast.error("Envie as duas fotos para continuar"); return; }
    if (!name.trim() || !email.trim()) { toast.error("Preencha seu nome e e-mail"); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) { toast.error("Informe um e-mail válido"); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/orders`, {
        gender, name: name.trim(), email: email.trim(),
        front_photo: front, profile_photo: profile,
      });
      try { localStorage.setItem("vz_order", data.order_id); } catch (e) { /* ignore */ }
      navigate(`/resultado?order_id=${data.order_id}`);
    } catch (e) {
      toast.error("Não foi possível continuar. Tente novamente.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F0F10] text-[#F5F1E8]">
      <header className="border-b border-white/10 bg-[#0F0F10]/85 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <button onClick={() => (step === 1 ? navigate("/") : setStep(1))} data-testid="back-btn" className="flex items-center gap-2 text-[#F5F1E8]/70 hover:text-[#C8A46A] transition-colors">
            <ArrowLeft className="w-4 h-4" /> Voltar
          </button>
          <div className="flex items-center gap-2 font-serif text-lg"><img src="/logo.png" alt="Visagismo IA" className="w-8 h-8 rounded-full object-cover ring-1 ring-[#C8A46A]/40" /> Visagismo<span className="text-[#C8A46A]"> IA</span></div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-12 md:py-16">
        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div key="s1" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
              <div className="text-center mb-10">
                <span className="text-xs tracking-[0.25em] uppercase text-[#C8A46A] font-semibold">Passo 1 de 2</span>
                <h1 className="font-serif text-4xl md:text-5xl mt-3">Para quem é a análise?</h1>
                <p className="text-[#F5F1E8]/60 mt-3">Escolha o perfil para uma análise de visagismo precisa.</p>
              </div>
              <div className="grid grid-cols-2 gap-5">
                {[{ id: "mulher", label: "Mulher", img: WOMAN_IMG },
                  { id: "homem", label: "Homem", img: MAN_IMG }].map((g) => (
                  <button key={g.id} data-testid={`gender-${g.id}`} onClick={() => pickGender(g.id)}
                    className="group relative rounded-3xl overflow-hidden aspect-[3/4] border border-white/10 hover:border-[#C8A46A] transition-all">
                    <img src={g.img} alt={g.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F10] via-[#0F0F10]/20 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6 text-[#F5F1E8] flex items-center justify-between">
                      <span className="font-serif text-2xl">{g.label}</span>
                      <span className="w-9 h-9 rounded-full bg-white/10 backdrop-blur flex items-center justify-center group-hover:bg-[#C8A46A] group-hover:text-[#0F0F10] transition-colors"><ScanFace className="w-4 h-4" /></span>
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div key="s2" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
              <div className="text-center mb-10">
                <span className="text-xs tracking-[0.25em] uppercase text-[#C8A46A] font-semibold">Passo 2 de 2</span>
                <h1 className="font-serif text-4xl md:text-5xl mt-3">Envie suas fotos</h1>
                <p className="text-[#F5F1E8]/60 mt-3">Uma foto de frente e uma de perfil, com boa iluminação.</p>
              </div>
              <div className="flex gap-5 max-w-lg mx-auto">
                <PhotoInput id="front" label="Foto de Frente" hint="Olhando para a câmera" value={front} onChange={setFront} />
                <PhotoInput id="profile" label="Foto de Perfil" hint="Rosto de lado (90°)" value={profile} onChange={setProfile} />
              </div>

              <Card className="mt-8 max-w-lg mx-auto p-6 rounded-2xl border-[#C8A46A]/25 bg-white/[0.03]">
                <div className="space-y-3 mb-5">
                  <Input data-testid="input-name" placeholder="Seu nome completo" value={name} onChange={(e) => setName(e.target.value)} className="bg-[#0F0F10] border-white/15 text-[#F5F1E8] placeholder:text-[#F5F1E8]/40" />
                  <Input data-testid="input-email" type="email" placeholder="Seu melhor e-mail" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-[#0F0F10] border-white/15 text-[#F5F1E8] placeholder:text-[#F5F1E8]/40" />
                  <p className="text-xs text-[#F5F1E8]/50">Use o mesmo e-mail no pagamento para liberarmos seu estudo automaticamente.</p>
                </div>
                <div className="flex items-center justify-between border-t border-white/10 pt-4">
                  <div>
                    <p className="font-serif text-lg">Estudo Profissional de Visagismo</p>
                    <p className="text-sm text-[#F5F1E8]/50">PDF completo · Acesso após pagamento</p>
                  </div>
                  <div className="text-right">
                    <span className="block text-sm text-[#F5F1E8]/40 line-through">R$450</span>
                    <span className="font-serif text-3xl text-[#C8A46A]">R${Number(price).toFixed(0)}</span>
                  </div>
                </div>
                <Button onClick={checkout} disabled={loading || !front || !profile} data-testid="checkout-btn"
                  className="mt-5 w-full rounded-full bg-[#C8A46A] text-[#0F0F10] hover:bg-[#d9b884] h-13 py-6 text-base font-semibold">
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processando…</> : <><Lock className="w-4 h-4 mr-2" /> Continuar para o pagamento</>}
                </Button>
                <p className="text-xs text-center text-[#F5F1E8]/50 mt-3 flex items-center justify-center gap-1"><Lock className="w-3 h-3" /> Pagamento seguro via Kiwify</p>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
